// Marketing call-to-action pieces for the free plan. They follow its public
// state (useFreePlan): "Start free" is shown only while the plan is open, and
// the small print names the free plan only then. While the state loads, and
// when it cannot be read, the plan counts as open.
import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useFreePlan } from '@/hooks/use-free-plan'
import { cn } from '@/lib/utils'

/** "Start free" -> /signup?plan=free. Renders nothing unless the free plan is open. */
export function StartFreeButton({
  variant = 'default',
  className,
  children = 'Start free',
  onClick,
}: {
  variant?: 'default' | 'outline'
  className?: string
  children?: ReactNode
  onClick?: () => void
}) {
  const free = useFreePlan()
  if (!free.isOpen) return null
  return (
    <Button size="lg" variant={variant} className={cn('h-11 px-5 text-[15px]', className)} asChild>
      <Link to="/signup" search={{ plan: 'free' }} onClick={onClick}>
        {children}
        <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />
      </Link>
    </Button>
  )
}

/** The line under a plan call-to-action: what is free, what paid costs, what the guarantee covers. */
export function PlanFinePrint({ className }: { className?: string }) {
  const free = useFreePlan()
  return (
    <p className={cn('text-sm text-muted-foreground', className)}>
      {free.isOpen ? (
        <>
          Free: <span className="num">{free.monitors}</span> monitors at <span className="num">{free.intervalSeconds}</span> s · paid from{' '}
          <span className="num">$3</span>/mo, 30-day money-back guarantee
        </>
      ) : (
        <>
          from <span className="num">$3</span>/mo · 30-day money-back guarantee
        </>
      )}
    </p>
  )
}
